/**
 * Noticing that the deployed build has moved on, and reloading rather than waiting to be told to.
 *
 * The portal is a page people leave open. A deployment replaces the hashed bundles and the
 * prerendered pages under them, and a tab that has been open since before it holds JavaScript whose
 * chunks no longer exist — which surfaces as a lazy route that will not load, or worse, as a page
 * that works while quietly speaking an older API. Asking the server which build it is, and
 * reloading when the answer changes, turns that into a reload nobody had to ask for.
 *
 * **The first answer is never a reason to reload.** It is what the tab started on, so it is stored
 * and nothing else happens; only a *change* from a value this tab has already seen means the ground
 * moved underneath it. Getting that wrong reloads every first-time visitor once, which looks exactly
 * like a broken site.
 *
 * `/api/version` rather than the `/api/v1` the rest of the client talks to: the deployed build's
 * identity is not a versioned resource, and it is deliberately reachable without a session.
 */
const VERSION_URL = '/api/version';

/** Per browser, not per tab: two tabs of the same site are running the same bundles. */
const STORAGE_KEY = 'ipodariger.build';

interface VersionResponse {
    service?: string;
    version?: string;
    /** The identity the decision rests on; `version` is for a human reading a bug report. */
    build?: string;
}

/**
 * Compares the running build against the deployed one.
 *
 * @returns `true` when a reload has been started, so a caller in the middle of something can stop.
 */
export async function checkDeployedBuild(): Promise<boolean> {
    // Server-rendered at build time, where there is no server to ask and no tab to reload.
    if (typeof window === 'undefined') {
        return false;
    }

    let deployed: string;

    try {
        const response = await fetch(VERSION_URL, { credentials: 'omit' });

        if (!response.ok) {
            return false;
        }

        const body = (await response.json()) as VersionResponse;

        if (typeof body.build !== 'string' || body.build === '') {
            return false;
        }

        deployed = body.build;
    } catch {
        // Offline, or an older server that has no such route. Neither is a reason to disturb
        // anybody: the page in front of the customer still works.
        return false;
    }

    const running = read();

    if (running === deployed) {
        return false;
    }

    write(deployed);

    // First sight of any build on this browser. There is nothing stale to clear and nothing to
    // reload — this *is* the build that just loaded.
    if (running === null) {
        return false;
    }

    await clearCachesAndReload();
    return true;
}

/**
 * Everything a service worker or the Cache API may still hand out, then a reload.
 *
 * Clearing before reloading is the point. A reload alone re-requests `index.html`, and a cache that
 * still holds the old one answers it — leaving the tab on the build it was already running and
 * doing so on every check from then on.
 */
async function clearCachesAndReload(): Promise<void> {
    try {
        if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((key) => caches.delete(key)));
        }

        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((registration) => registration.unregister()));
        }
    } catch {
        // A browser that refuses either of these still gets the reload below, which is the part
        // that matters; the caches are what make it stick.
    }

    window.location.reload();
}

/** Storage throws in a locked-down browser, and a version check is never worth an exception. */
function read(): string | null {
    try {
        return window.localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

function write(build: string): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, build);
    } catch {
        // Nothing is remembered, so nothing reloads. Better than failing the page over a
        // preference the visitor has set on purpose.
    }
}

/**
 * Runs the check now and again whenever the tab comes back to the front.
 *
 * On focus rather than on a timer: the tab that most needs this is the one somebody left open and
 * came back to, and that is precisely the moment it becomes visible again. A timer would ask the
 * same question of a background tab nobody is looking at, all day, for every open tab.
 */
export function watchDeployedBuild(): void {
    if (typeof window === 'undefined') {
        return;
    }

    void checkDeployedBuild();

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            void checkDeployedBuild();
        }
    });
}
